/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {getRoleIds, validateRoleGroups} from '@ion/core/src/entities/Roles';
import {error} from '@ion/core/src/logger/logger';
import {
  bulkSearchRoles,
  createBulkEntities,
  uploadManufacturingDevices
} from '@ion/core/src/services/Connect';
import {
  createBulkUsers,
  UserContext
} from '@ion/core/src/services/MicroServices';
import {
  Asset,
  CTCEntityResponseWrapper,
  Device,
  Entity,
  GenericObject,
  User
} from '@ion/core/src/types/entities';
import {ExtendedResults} from '@ion/core/src/types/services';
import {getAssetImportErrors} from '@ion/core/src/xlsx/AssetTemplate';
import {getDeviceImportErrors} from '@ion/core/src/xlsx/DeviceTemplate';
import {getManufacturedDeviceImportErrors} from '@ion/core/src/xlsx/ManufacturedDevicesTemplate';
import {getBulkUserImportErrors} from '@ion/core/src/xlsx/UserTemplate';
import {
  createRoleSearchTerms,
  getAccountId,
  validateAssets,
  validateDevices,
  validateManufacturedDevices
} from '../../helpers';
import {toggleUserPrompt} from './user-prompt';

// invoked when an import is initiated ...
export const IMPORT_REQUEST = 'IMPORT_REQUEST';

// invoked when an import has completed ...
export const IMPORT_RESPONSE = 'IMPORT_RESPONSE';

// invoked when an import has failed ...
export const IMPORT_ERROR = 'IMPORT_ERROR';

// invoked when an import is confirmed after completion ...
export const IMPORT_CONFIRM = 'IMPORT_CONFIRM';

// invoked when an import has empty device identifiers ...
export const IMPORT_VALIDATION = 'IMPORT_VALIDATION';

export const importConfirm = (entity) => ({
  type : IMPORT_CONFIRM,
  entity
});

export const findUserByName = (name: string, items: any) => {
  return items?.find((item) => item.username === name);
};

export const constructRequest = (
  {errors, results}: GenericObject,
  resolvedUsers: User,
  dispatch
) => {
  const result = {
    errors  : [],
    results : []
  };
  errors?.forEach(({error}) => {
    const exceptions = error?.description.split(': ');
    const description = exceptions?.splice(0, 1)?.toString() || '';
    if (exceptions?.length) {
      const users = exceptions[0].split(',');
      users?.forEach((user) => {
        const resolvedUser = findUserByName(user, resolvedUsers);
        result.errors.push({...resolvedUser, description, code: error?.code});
      });
    } else {
      dispatch(toggleUserPrompt({message: description}));
    }
  });
  results?.forEach(({task}) => {
    task?.others?.users?.forEach((user) => {
      const resolvedUser = findUserByName(user.username, resolvedUsers);
      result.results.push({...resolvedUser});
    });
  });
  return result;
};

/**
 * Provides the User Export Action Creator which manages retrieving all users
 * and converting the retrieved users to a CSV for subsequent download.
 */
export const importUsers = (
  users: CTCEntityResponseWrapper<User>[],
  context?: UserContext
) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    const entity = Entity.user;
    const {
      globalGroup: {globalGroup}
    } = getState();
    const accountId = getAccountId(getState);
    let invalidUsers;
    const resolvedUsers: any = [];
    dispatch({type: IMPORT_REQUEST, entity});
    try {
      let resolved;
      const unresolved = [];
      const roleSearch = createRoleSearchTerms(users, accountId);
      let roles;
      if (roleSearch) {
        roles = await bulkSearchRoles(roleSearch);
      }
      invalidUsers = unresolved;

      if (roles) {
        const {valid, invalid} = validateRoleGroups(users, roles);
        invalidUsers = invalidUsers.concat(invalid);
        resolved = valid;
      }

      resolvedUsers.accountId = accountId;
      resolved.forEach(({user}) => {
        const roles = getRoleIds(user.roles);
        user.roles = roles.toString();
        resolvedUsers.push(user);
      });
      const response: any = await createBulkUsers(resolvedUsers);
      const result: ExtendedResults = Object.assign({}, response);
      const processedResult = getBulkUserImportErrors(
        invalidUsers,
        result,
        globalGroup
      );
      dispatch({
        type   : IMPORT_RESPONSE,
        entity,
        result : processedResult,
        valid  : !(result?.errors?.length > 0)
      });
    } catch (err) {
      dispatch({type: IMPORT_ERROR, entity});
      return error(err);
    }
  };
};

/**
 * Provides the Device Export Action Creator which manages retrieving all devices
 * and converting the retrieved devices to a CSV for subsequent download.
 */
export const importDevices = (devices: CTCEntityResponseWrapper<Device>[]) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    const entity = Entity.device;
    const {
      globalGroup: {globalGroup}
    } = getState();
    const accountId = getAccountId(getState);
    let invalidDevices = [];
    dispatch({type: IMPORT_REQUEST, entity});
    try {
      const {valid, invalid} = validateDevices(devices);
      invalidDevices = invalid;
      const resolvedDevices = valid.map(({device}) => device);
      const response = await createBulkEntities(
        accountId,
        entity,
        resolvedDevices
      );
      const result: ExtendedResults = Object.assign({}, response);
      const processedResult = getDeviceImportErrors(
        invalidDevices,
        result,
        globalGroup
      );
      dispatch({
        type   : IMPORT_RESPONSE,
        entity,
        result : processedResult,
        valid  : !(result?.errors?.length > 0)
      });
    } catch (err) {
      dispatch({type: IMPORT_ERROR, entity});
      return error(err);
    }
  };
};

export const importDevicesValidation = (isValidDeviceIdentifiers: boolean) => {
  return (dispatch: Function) => {
    const entity = Entity.device;
    try {
      dispatch({
        type            : IMPORT_VALIDATION,
        entity,
        showErrorDialog : isValidDeviceIdentifiers
      });
    } catch (err) {
      dispatch({type: IMPORT_ERROR, entity});
      return error(err);
    }
  };
};

/**
 * Provides the Asset Export Action Creator which manages retrieving all assets
 * and converting the retrieved assets to a CSV for subsequent download.
 */
export const importAssets = (assets: CTCEntityResponseWrapper<Asset>[]) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    const entity = Entity.asset;
    dispatch({type: IMPORT_REQUEST, entity});
    try {
      const {
        globalGroup: {globalGroup}
      } = getState();
      const accountId = getAccountId(getState);
      const {valid, invalid: invalidAssets} = validateAssets(assets);
      const resolvedAssets = valid.map(({asset}) => asset);
      const response = await createBulkEntities(
        accountId,
        entity,
        resolvedAssets
      );
      const result: ExtendedResults = Object.assign({}, response);
      const processedResult = getAssetImportErrors(
        invalidAssets,
        result,
        globalGroup
      );
      dispatch({
        type   : IMPORT_RESPONSE,
        entity,
        result : processedResult,
        valid  : !(result?.errors?.length > 0)
      });
    } catch (err) {
      dispatch({type: IMPORT_ERROR, entity});
      return error(err);
    }
  };
};

export const importManufacturedDevices = (manufacturedDevices) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    const entity = Entity.manufacturedDevice;
    const {
      globalGroup: {globalGroup}
    } = getState();
    let invalidDevices = [];
    dispatch({type: IMPORT_REQUEST, entity});
    try {
      const {valid, invalid} = validateManufacturedDevices(manufacturedDevices);
      invalidDevices = invalid;
      const accountId = getAccountId(getState);
      const response = await uploadManufacturingDevices(
        accountId,
        entity,
        valid
      );
      const result: ExtendedResults = Object.assign({}, response);
      const processedResult = getManufacturedDeviceImportErrors(
        invalidDevices,
        result,
        globalGroup
      );
      dispatch({
        type   : IMPORT_RESPONSE,
        entity,
        result : processedResult,
        valid  : !(result?.errors?.length > 0)
      });
    } catch (err) {
      dispatch({type: IMPORT_ERROR, entity});
      return error(err);
    }
  };
};

export const importManufacturedDevicesValidation = (
  isValidDeviceIdentifiers: boolean
) => {
  return (dispatch: Function) => {
    const entity = Entity.manufacturing;
    try {
      dispatch({
        type            : IMPORT_VALIDATION,
        entity,
        showErrorDialog : isValidDeviceIdentifiers
      });
    } catch (err) {
      dispatch({type: IMPORT_ERROR, entity});
      return error(err);
    }
  };
};
